import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, MailCheck } from "lucide-react";

export function ForgotPasswordDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const reset = (v) => {
    setOpen(v);
    if (!v) {
      setSent(false);
      setEmail("");
      setError("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <button type="button" data-testid="forgot-password-link" className="font-mono text-[11px] uppercase tracking-wider text-primary transition-colors hover:text-blue-300 hover:underline">
          Forgot?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm bg-card" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Reset your password</DialogTitle>
          <DialogDescription>Enter your dealership email and we'll send a secure link to set a new password.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="space-y-4 py-2 text-center" data-testid="forgot-sent">
            <MailCheck className="mx-auto h-10 w-10 text-emerald-400" />
            <p className="text-sm text-muted-foreground">If an account exists for that email, a reset link has been sent. Please check your inbox.</p>
            <Button className="w-full" onClick={() => reset(false)} data-testid="forgot-close">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" data-testid="forgot-email-input" className="bg-secondary/40" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dealership.com" required />
            </div>
            {error && <p className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
            <DialogFooter>
              <Button type="submit" disabled={loading} className="w-full" data-testid="forgot-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reset link
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
